import NavResponsiveModal, {
    ModalActions,
    ModalContent,
    ModalHeader,
} from '@/components/Navigation/NavResponsiveModal';
import React from 'react';
import { css } from '@emotion/react';
import { PrimaryButton, SecondaryButton } from '@/styles/blocks';
import { colors } from '@/styles';
import { Body1 } from '../blocks';
import { lastStoredLocation } from '../ManifestGrid/utils';

// Body1 is nowrap — it serves single-line card fields.
const wrapped = css`
    text-wrap: wrap;
    line-height: 1.5;
`;

const warning = css`
    text-wrap: wrap;
    line-height: 1.5;
    color: ${colors.reds.primary};
    margin-top: 0.75rem;
`;

const checking = css`
    text-wrap: wrap;
    line-height: 1.5;
    color: ${colors.greys[1]};
    margin-top: 0.75rem;
`;

// Un-receive is a straight yes/no; un-stage asks where the pallets should land.
const Unwind = ({ target, loading, callbacks }) => {
    const {
        mode,
        order,
        pallets = [],
        overspill,
        affectedOrders = [],
        otherOrderNumbers = [],
        lockedOrderNumbers = [],
    } = target || {};
    const onClose = () => callbacks.closeUnwind();

    const isUnreceive = mode === 'UNRECEIVE';
    const orderLabel = order?.order_number || order?.po_number || 'this order';
    const itemCount = (order?.manifestItems || []).length;
    const restorable = pallets.filter((pallet) => lastStoredLocation(pallet)).length;

    // The pallet lookup decides the blast radius — nothing is confirmable until it lands.
    const busy = loading.palletLoading || overspill === null;

    // Only un-receive. Un-stage does not touch the ON_HAND determination, and an allocated item that
    // keeps its ITEM_STAGED event still renders as "Allocated", which is correct.
    const locked = isUnreceive && lockedOrderNumbers.length > 0;

    const run = (unstageMode) => {
        const action = isUnreceive
            ? callbacks.unreceiveOrder({ orders: affectedOrders, pallets })
            : callbacks.unstagePallets(pallets, unstageMode);
        return Promise.resolve(action).then(() => onClose());
    };

    return (
        <NavResponsiveModal open={!!target} onClose={onClose}>
            <ModalHeader title={isUnreceive ? 'Un-receive Order' : 'Un-stage Order'} onClose={onClose} />
            <ModalContent
                css={css`
                    width: 460px;
                `}
            >
                {isUnreceive ? (
                    <Body1 css={wrapped}>
                        Send all {itemCount} {itemCount === 1 ? 'item' : 'items'} on <b>{orderLabel}</b> back to Not
                        Received? This also clears their warehouse locations and clears the order's received status
                        and date.
                    </Body1>
                ) : (
                    <Body1 css={wrapped}>
                        Un-stage {pallets.length} {pallets.length === 1 ? 'pallet' : 'pallets'} on{' '}
                        <b>{orderLabel}</b>. Staging cleared their locations — choose where they land.
                        {restorable > 0 && restorable < pallets.length
                            ? ` Only ${restorable} have a previous location on record; the rest go back to Received.`
                            : ''}
                    </Body1>
                )}

                {locked ? (
                    <Body1 css={warning}>
                        Un-receive isn&apos;t supported for forward-stocked inventory yet.{' '}
                        {lockedOrderNumbers.length === 1 ? (
                            <>
                                <b>{lockedOrderNumbers[0]}</b> is part of the forward stocking flow, so its
                            </>
                        ) : (
                            <>
                                <b>{lockedOrderNumbers.join(', ')}</b> are part of the forward stocking flow, so their
                            </>
                        )}{' '}
                        warehouse inventory status is derived from tracking events that un-receive cannot reverse.
                    </Body1>
                ) : null}

                {overspill === null ? (
                    <Body1 css={checking}>Checking what else is on these pallets…</Body1>
                ) : overspill > 0 ? (
                    <Body1 css={warning}>
                        These pallets are shared. {overspill} {overspill === 1 ? 'item' : 'items'} on{' '}
                        {otherOrderNumbers.length ? <b>{otherOrderNumbers.join(', ')}</b> : 'other orders'} will also be{' '}
                        {isUnreceive ? 'un-received' : 'un-staged'} — warehouse status lives on the pallet, not the
                        item.
                        {isUnreceive
                            ? ` Their received status and date are cleared too, so nothing is left half-undone.`
                            : ''}
                    </Body1>
                ) : null}
            </ModalContent>
            <ModalActions>
                <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                {isUnreceive ? (
                    <PrimaryButton red disabled={busy || locked} onClick={() => run()}>
                        Un-receive
                    </PrimaryButton>
                ) : (
                    <>
                        <SecondaryButton disabled={busy} onClick={() => run('RECEIVED')}>
                            Back to Received
                        </SecondaryButton>
                        <PrimaryButton disabled={busy || restorable === 0} onClick={() => run('STORED')}>
                            Back to previous location
                        </PrimaryButton>
                    </>
                )}
            </ModalActions>
        </NavResponsiveModal>
    );
};

export default Unwind;
